"use client";
import React from "react";
import Link from "next/link";
import ResourceCard from "./ResourceCard";
import styles from "./resources.module.css";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export interface Resource {
  id: string;
  title: string;
  description: string;
  icon?: string;
  iconAlt: string;
}

export interface ResourcesProps {
  title?: string;
  subtitle?: string;
  ctaTitle?: string;
  ctaDescription?: string;
  primaryButtonText?: string;
  primaryButtonHref?: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  resources?: Resource[];
}

const fallbackResources: Resource[] = [
  {
    id: "templates",
    title: "Templates & Guides",
    description:
      "Sprint calendars, DoR/DoD checklists, refinement agendas, and story templates",
    icon: "",
    iconAlt: "templates and guides",
  },
  {
    id: "videos",
    title: "Video Walkthroughs",
    description:
      "Step-by-step guides for implementing Agile practices and configuring tools",
    icon: "",
    iconAlt: "video walkthroughs",
  },
  {
    id: "exports",
    title: "Tool Exports",
    description:
      "Ready-to-import solutions for Jira, Azure DevOps, Trello, and ClickUp",
    icon: "",
    iconAlt: "tool exports",
  },
  {
    id: "configurators",
    title: "Configurators",
    description:
      "Automated setup for project management tools with best-practice workflows",
    icon: "",
    iconAlt: "configurators",
  },
];

const Resources: React.FC<ResourcesProps> = ({
  title,
  subtitle,
  ctaTitle,
  ctaDescription,
  primaryButtonText,
  primaryButtonHref,
  secondaryButtonText,
  secondaryButtonHref,
  resources: resourcesProp,
}) => {
  // Use fallback resources if none provided or empty array
  const resources = resourcesProp && resourcesProp.length > 0 ? resourcesProp : fallbackResources;
  
  // Use default values only if prop is undefined (not explicitly set to empty string)
  const displayTitle = title ?? "Resources & Tools";
  const displaySubtitle = subtitle ?? "A library of templates, guides, and automation tools built to accelerate team setup and reduce delivery friction.";
  const displayCtaTitle = ctaTitle ?? "Ready-to-Use Agile Resources";
  const displayCtaDescription = ctaDescription ?? "Choose from Starter, Pro, or Elite packs. Get instant access to templates, videos, and tools that help teams deliver faster.";
  const displayPrimaryButtonText = primaryButtonText ?? "View All Resources";
  const displayPrimaryButtonHref = primaryButtonHref ?? "/resources";
  const displaySecondaryButtonText = secondaryButtonText ?? "See Pricing";
  const displaySecondaryButtonHref = secondaryButtonHref ?? "/pricing";
  const [sectionRef, isVisible] = useScrollAnimation();

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`w-full py-24 px-6 lg:px-8 ${isVisible ? styles.sectionAnimation : ""}`}
      aria-label="Resources and tools"
    >
      <div className="mx-auto max-w-7xl flex flex-col gap-12">
        {/* Header */}
        <header className={`mx-auto text-center max-w-3xl ${isVisible ? styles.headerAnimation : ""}`}>
          <h2 className="font-assistant font-semibold text-white text-5xl leading-[46px] mb-5">
            {displayTitle}
          </h2>
          {displaySubtitle ? (
            <p className="text-lg leading-7 font-normal text-slate-300 max-w-xl mx-auto">
              {displaySubtitle}
            </p>
          ) : null}
        </header>

        {/* Resource Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {resources.map((resource) => (
            <div key={resource.id} className={styles.cardAnimation}>
              <ResourceCard
                title={resource.title}
                description={resource.description}
                icon={resource.icon}
                iconAlt={resource.iconAlt}
              />
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        {(displayCtaTitle || displayCtaDescription || displayPrimaryButtonText || displaySecondaryButtonText) && (
          <div
            className={`rounded-lg p-8 lg:p-12 text-center border ${styles.ctaBackground} ${styles.ctaBorder} ${styles.ctaAnimation}`}
          >
            {displayCtaTitle && (
              <h3 className="font-assistant font-semibold text-white text-2xl lg:text-[32px] leading-[46px] mb-4">
                {displayCtaTitle}
              </h3>
            )}
            {displayCtaDescription && (
              <p className="text-slate-300 text-base leading-6 max-w-xl mx-auto mb-6">
                {displayCtaDescription}
              </p>
            )}
            {(displayPrimaryButtonText || displaySecondaryButtonText) && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {displayPrimaryButtonText && displayPrimaryButtonHref && (
                  <Link
                    href={displayPrimaryButtonHref}
                    className={`inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-semibold text-white ${styles.primaryButton}`}
                  >
                    {displayPrimaryButtonText}
                  </Link>
                )}
                {displaySecondaryButtonText && displaySecondaryButtonHref && (
                  <Link
                    href={displaySecondaryButtonHref}
                    className={`inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-semibold text-white border-2 ${styles.secondaryButton}`}
                  >
                    {displaySecondaryButtonText}
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Resources;
